'use client';

/**
 * The multicade LTO rail — ONE component, rendered on BOTH the play tab
 * (PartnerCatalogue) and the store tab (MulticadeStoreShell).
 *
 * It exists because those two surfaces had drifted: the store rendered StoreV2's
 * LTOCarousel while the play tab rendered nothing at all, so the same active offer appeared
 * on one tab and not the other. Sharing the component means they cannot diverge again.
 *
 * The section (heading + band + padding) lives in ArcadeLtoRailInner, loaded ssr:false so
 * the window/Dimensions-reading LTOCarousel graph never evaluates on the server, and so the
 * inner can consult the offers hook and render NOTHING when there are no eligible offers —
 * the band never shows as an empty strip. QueryProvider wraps it because both the offers
 * hook and LTOCarousel self-fetch via react-query.
 */

import dynamic from 'next/dynamic';
import {useEffect} from 'react';
import {useQueryClient} from '@sdk-mobile';
import QueryProvider from '@/app/(app)/games/[gid]/_src/QueryProvider';

const ArcadeLtoRailInner = dynamic(() => import('./ArcadeLtoRailInner'), {ssr: false});

/**
 * Warms the banner-interstitials query at hydration — BEFORE the ssr:false LTOCarousel chunk
 * finishes loading. The offers fetch used to be serialized AFTER that lazy chunk (the query
 * only mounts once LTOCarousel does), so the whole rail waited on chunk-load THEN fetch while
 * Live Events (server-rendered) showed instantly. Prefetching here overlaps the fetch with the
 * chunk load, so the cards render as soon as the chunk lands. Same queryKey as
 * useBannerInterstitials, so react-query serves the cached result (no double fetch). The
 * InterstitialsManager import is dynamic + in an effect so it stays off the server and out of
 * the main bundle (it's part of the window-reading graph ssr:false exists to defer).
 */
function LtoPrefetch() {
  const qc = useQueryClient();
  useEffect(() => {
    let cancelled = false;
    import('InterstitialsManager')
      .then((m) => {
        if (cancelled) return;
        qc.prefetchQuery({
          queryKey: ['activeInterstitials'],
          queryFn: (m as any).default.fetchBannerInterstitials,
        });
      })
      .catch(() => {
        /* prefetch is best-effort; the hook still fetches on mount */
      });
    return () => {
      cancelled = true;
    };
  }, [qc]);
  return null;
}

export default function ArcadeLtoRail() {
  return (
    <QueryProvider>
      <LtoPrefetch />
      <ArcadeLtoRailInner />
    </QueryProvider>
  );
}
